import React from "react";
import { FaArrowRight } from "react-icons/fa6";

// Shared building blocks for the Tailwind home page sections.

export const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-7xl px-[16px] sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

export const Badge = ({ children, lined = false }) => {
  const pill = (
    <span className="inline-flex items-center gap-2 rounded-full bg-brand-soft px-[14px] py-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-ink/80">
      {!lined && <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />}
      {children}
    </span>
  );
  if (!lined) return pill;
  return (
    <div className="flex items-center justify-center gap-[14px]">
      <span className="h-px w-12 bg-slate-300" />
      {pill}
      <span className="h-px w-12 bg-slate-300" />
    </div>
  );
};

export const SectionHeading = ({ badge, lined, title, highlight, subtitle, highlightBreak = true, className = "" }) => (
  <div className={`mx-auto max-w-3xl text-center ${className}`}>
    {badge && <Badge lined={lined}>{badge}</Badge>}
    <h2 className="mt-[18px] text-[30px] font-bold leading-tight tracking-tight text-ink sm:text-[40px] lg:text-[46px]">
      {title}
      {highlight && (
        <>
          {highlightBreak ? <br /> : " "}
          <span className="text-gradient">{highlight}</span>
        </>
      )}
    </h2>
    {subtitle && <p className="mx-auto mt-[16px] max-w-2xl text-[15px] leading-relaxed text-slate-500 sm:text-[16px]">{subtitle}</p>}
  </div>
);

export const GradientButton = ({ href = "#", children, className = "", type, onClick, disabled }) => {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-7 py-[14px] text-[15px] font-semibold text-white shadow-glow transition hover:-translate-y-0.5 hover:brightness-110 disabled:opacity-60 ${className}`;
  const inner = (
    <>
      {children}
      <FaArrowRight className="text-[14px] transition group-hover:translate-x-1" />
    </>
  );
  if (type) {
    return (
      <button type={type} onClick={onClick} disabled={disabled} className={`border-0 ${cls}`}>
        {inner}
      </button>
    );
  }
  return (
    <a href={href} className={cls}>
      {inner}
    </a>
  );
};

// Tailwind needs full class names at build time, so colour themes are spelled out here.
export const tones = {
  blue: { soft: "bg-blue-50", text: "text-blue-600", ring: "ring-blue-100", dot: "bg-blue-500", glow: "from-blue-100" },
  pink: { soft: "bg-fuchsia-50", text: "text-fuchsia-600", ring: "ring-fuchsia-100", dot: "bg-fuchsia-500", glow: "from-fuchsia-100" },
  violet: { soft: "bg-violet-50", text: "text-violet-600", ring: "ring-violet-100", dot: "bg-violet-500", glow: "from-violet-100" },
  green: { soft: "bg-emerald-50", text: "text-emerald-600", ring: "ring-emerald-100", dot: "bg-emerald-500", glow: "from-emerald-100" },
  orange: { soft: "bg-orange-50", text: "text-orange-500", ring: "ring-orange-100", dot: "bg-orange-500", glow: "from-orange-100" },
  red: { soft: "bg-rose-50", text: "text-rose-500", ring: "ring-rose-100", dot: "bg-rose-500", glow: "from-rose-100" },
};

export const IconTile = ({ icon: Icon, tone = "blue", size = "md" }) => {
  const t = tones[tone];
  const s = size === "lg" ? "h-[60px] w-[60px] text-[26px] rounded-2xl" : "h-[48px] w-[48px] text-[20px] rounded-xl";
  return (
    <span className={`inline-flex shrink-0 items-center justify-center ${s} ${t.soft} ${t.text}`}>
      <Icon />
    </span>
  );
};

export const ArrowCircle = ({ tone = "blue", className = "", size = "md" }) => {
  const t = tones[tone];
  const sz = size === "lg" ? "h-[52px] w-[52px] text-[18px]" : "h-[38px] w-[38px] text-[14px]";
  return (
    <span className={`inline-flex shrink-0 items-center justify-center rounded-full ${sz} ${t.soft} ${t.text} transition group-hover:translate-x-1 ${className}`}>
      <FaArrowRight />
    </span>
  );
};
